// cspell:disable
/**
 * Rich KG sample data for offline canvas development.
 *
 * Pipeline tiers (left → right in canvas):
 *   dataSource → schema (optional) → table → ontology → metric → report → dataStory → artifact
 *
 * Positions are placeholders; `getDagreLayout` assigns real layout on mount.
 * Includes `layoutStressSampleData` extras for grid overflow + viewport column wrap.
 *
 * Enable mock API: `REACT_APP_KG_GRAPH_API=mock` in `.env`
 * See DATA_LOADING_GUIDE.md for how to add / replace nodes.
 */

import { getEdgeColor } from "../utils/edgeColors";
import { resolveEdgeHandlesForEndpoints } from "../utils/kgEdgeHandles";
import type { KgFlowEdge, KgFlowNode, KgFlowNodeType } from "../types";
import {
  stressArtifacts,
  stressDataStories,
  stressEdgeDefs,
  stressMetrics,
  stressOntologies,
  stressReports,
  stressSchemas,
  stressTables,
} from "./layoutStressSampleData";

// ─── Data Sources (6) ────────────────────────────────────────────────────────
const dataSources: KgFlowNode[] = [
  {
    id: "ds-snowflake",
    type: "dataSource",
    position: { x: 0, y: 0 },
    data: {
      title: "Snowflake",
      subtitle: "Data Warehouse",
      logoUrl: "/logos/snowflake.svg",
      details: {
        type: "Data warehouse",
        status: "connected",
        connectedOn: "2024-01-15",
        instances: 2,
        instanceNames: ["Analytics", "Staging"],
        externalUrl: "https://app.snowflake.com/",
      },
    },
  },
  {
    id: "ds-tableau",
    type: "dataSource",
    position: { x: 0, y: 0 },
    data: {
      title: "Tableau",
      subtitle: "BI Tool",
      logoUrl: "/logos/tableau.svg",
      details: {
        type: "BI tool",
        status: "connected",
        connectedOn: "2024-01-15",
        instances: 2,
        instanceNames: ["Sales", "Production"],
        tables: [
          { name: "orders", primaryKey: "order_id", isLinked: true },
          { name: "products", primaryKey: "product_id", isLinked: true },
          { name: "customers", primaryKey: "customer_id", isLinked: true },
        ],
        reports: [
          {
            title: "Sales trend dashboard",
            connectedOn: "Jan 15, 2024",
            isLinked: true,
          },
          {
            title: "Regional performance",
            connectedOn: "Jan 15, 2024",
            isLinked: true,
          },
          {
            title: "Category mix",
            connectedOn: "Jan 15, 2024",
            isLinked: true,
          },
        ],
        usedInSections: [
          {
            id: "ontologies",
            label: "Ontologies",
            nodeType: "ontology",
            items: [
              { label: "Order" },
              { label: "Region" },
              { label: "Product" },
            ],
          },
          {
            id: "dataStories",
            label: "Data stories",
            nodeType: "dataStory",
            items: [{ label: "Q3 revenue review" }],
          },
        ],
        externalUrl: "https://online.tableau.com/",
      },
    },
  },
  {
    id: "ds-bigquery",
    type: "dataSource",
    position: { x: 0, y: 0 },
    data: {
      title: "BigQuery",
      subtitle: "Cloud Warehouse",
      logoUrl: "/logos/bigquery.svg",
      details: {
        type: "Cloud warehouse",
        status: "connected",
        connectedOn: "2024-02-02",
        instances: 1,
        instanceNames: ["Default"],
        externalUrl: "https://console.cloud.google.com/bigquery",
      },
    },
  },
  {
    id: "ds-dbt",
    type: "dataSource",
    position: { x: 0, y: 0 },
    data: {
      title: "dbt",
      subtitle: "Transform Layer",
      logoUrl: "/logos/dbt.svg",
      details: {
        type: "Transform layer",
        status: "connected",
        connectedOn: "2024-03-11",
        instances: 1,
        instanceNames: ["Cloud"],
        externalUrl: "https://cloud.getdbt.com/",
      },
    },
  },
  {
    id: "ds-s3",
    type: "dataSource",
    position: { x: 0, y: 0 },
    data: {
      title: "S3 Data Lake",
      subtitle: "Object Storage",
      logoUrl: "/logos/s3.svg",
      details: {
        type: "Object storage",
        status: "connected",
        connectedOn: "2023-11-20",
        instances: 3,
        instanceNames: ["Raw", "Curated", "Archive"],
      },
    },
  },
  {
    id: "ds-postgres",
    type: "dataSource",
    position: { x: 0, y: 0 },
    data: {
      title: "PostgreSQL",
      subtitle: "OLTP Database",
      logoUrl: "/logos/postgres.svg",
      details: {
        type: "OLTP database",
        status: "pending",
        connectedOn: "2024-04-09",
        instances: 2,
        instanceNames: ["Primary", "Replica"],
      },
    },
  },
];

// ─── Schemas (3) ─────────────────────────────────────────────────────────────
// Mixed hierarchy: only SOME data sources expose schemas. Snowflake has two
// schemas, Postgres has one; every other data source connects to tables directly.
const schemas: KgFlowNode[] = [
  {
    id: "sch-sf-analytics",
    type: "schema",
    position: { x: 0, y: 0 },
    data: { title: "Analytics", subtitle: "Schema" },
  },
  {
    id: "sch-sf-staging",
    type: "schema",
    position: { x: 0, y: 0 },
    data: { title: "Staging", subtitle: "Schema" },
  },
  {
    id: "sch-pg-public",
    type: "schema",
    position: { x: 0, y: 0 },
    data: { title: "Public", subtitle: "Schema" },
  },
];

// ─── Tables (22) ─────────────────────────────────────────────────────────────
const tables: KgFlowNode[] = [
  {
    id: "tbl-orders",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Orders",
      details: {
        externalUrl: "https://app.snowflake.com/",
      },
      fields: [
        { name: "order_id", keyType: "PK" },
        { name: "customer_id", keyType: "FK" },
        { name: "order_date" },
        { name: "status" },
        { name: "total" },
      ],
    },
  },
  {
    id: "tbl-products",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Products",
      fields: [
        { name: "product_id", keyType: "PK" },
        { name: "product_name" },
        { name: "category" },
        { name: "supplier_id", keyType: "FK" },
        { name: "price" },
      ],
    },
  },
  {
    id: "tbl-customers",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Customers",
      fields: [
        { name: "customer_id", keyType: "PK" },
        { name: "name" },
        { name: "email" },
        { name: "region" },
        { name: "segment" },
      ],
    },
  },
  {
    id: "tbl-inventory",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Inventory",
      fields: [
        { name: "item_id", keyType: "PK" },
        { name: "product_id", keyType: "FK" },
        { name: "warehouse_id", keyType: "FK" },
        { name: "qty_on_hand" },
      ],
    },
  },
  {
    id: "tbl-suppliers",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Suppliers",
      fields: [
        { name: "supplier_id", keyType: "PK" },
        { name: "name" },
        { name: "country" },
        { name: "lead_days" },
      ],
    },
  },
  {
    id: "tbl-transactions",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Transactions",
      fields: [
        { name: "txn_id", keyType: "PK" },
        { name: "order_id", keyType: "FK" },
        { name: "amount" },
        { name: "payment_method" },
        { name: "txn_date" },
      ],
    },
  },
  {
    id: "tbl-employees",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Employees",
      fields: [
        { name: "emp_id", keyType: "PK" },
        { name: "dept_id", keyType: "FK" },
        { name: "name" },
        { name: "role" },
        { name: "hire_date" },
      ],
    },
  },
  {
    id: "tbl-departments",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Departments",
      fields: [
        { name: "dept_id", keyType: "PK" },
        { name: "dept_name" },
        { name: "manager_id", keyType: "FK" },
      ],
    },
  },
  {
    id: "tbl-campaigns",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Campaigns",
      fields: [
        { name: "campaign_id", keyType: "PK" },
        { name: "name" },
        { name: "channel" },
        { name: "budget" },
        { name: "start_date" },
      ],
    },
  },
  {
    id: "tbl-returns",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Returns",
      fields: [
        { name: "return_id", keyType: "PK" },
        { name: "order_id", keyType: "FK" },
        { name: "reason" },
        { name: "return_date" },
      ],
    },
  },
  {
    id: "tbl-shipments",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Shipments",
      fields: [
        { name: "shipment_id", keyType: "PK" },
        { name: "order_id", keyType: "FK" },
        { name: "carrier" },
        { name: "status" },
        { name: "eta" },
      ],
    },
  },
  {
    id: "tbl-warehouses",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Warehouses",
      fields: [
        { name: "warehouse_id", keyType: "PK" },
        { name: "location" },
        { name: "capacity" },
      ],
    },
  },
  {
    id: "tbl-invoices",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Invoices",
      fields: [
        { name: "invoice_id", keyType: "PK" },
        { name: "order_id", keyType: "FK" },
        { name: "issued_date" },
        { name: "due_date" },
        { name: "amount" },
      ],
    },
  },
  {
    id: "tbl-payments",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Payments",
      fields: [
        { name: "payment_id", keyType: "PK" },
        { name: "invoice_id", keyType: "FK" },
        { name: "paid_date" },
        { name: "amount" },
      ],
    },
  },
  {
    id: "tbl-subscriptions",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Subscriptions",
      fields: [
        { name: "sub_id", keyType: "PK" },
        { name: "customer_id", keyType: "FK" },
        { name: "plan" },
        { name: "status" },
        { name: "renewed_at" },
      ],
    },
  },
  {
    id: "tbl-events",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Events",
      fields: [
        { name: "event_id", keyType: "PK" },
        { name: "session_id", keyType: "FK" },
        { name: "event_type" },
        { name: "timestamp" },
      ],
    },
  },
  {
    id: "tbl-sessions",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Sessions",
      fields: [
        { name: "session_id", keyType: "PK" },
        { name: "customer_id", keyType: "FK" },
        { name: "device" },
        { name: "duration_s" },
      ],
    },
  },
  {
    id: "tbl-page-views",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Page Views",
      fields: [
        { name: "view_id", keyType: "PK" },
        { name: "session_id", keyType: "FK" },
        { name: "url" },
        { name: "viewed_at" },
      ],
    },
  },
  {
    id: "tbl-sales-report",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Sales Report",
      details: {
        externalUrl: "https://online.tableau.com/",
      },
      fields: [
        { name: "sales_trend" },
        { name: "sales_vs_expense" },
        { name: "order_values" },
        { name: "revenue" },
        { name: "customer_id", keyType: "FK" },
      ],
    },
  },
  {
    id: "tbl-product-catalog",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Product Catalog",
      fields: [
        { name: "catalog_id", keyType: "PK" },
        { name: "product_id", keyType: "FK" },
        { name: "description" },
        { name: "tags" },
      ],
    },
  },
  {
    id: "tbl-order-items",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Order Items",
      fields: [
        { name: "item_id", keyType: "PK" },
        { name: "order_id", keyType: "FK" },
        { name: "product_id", keyType: "FK" },
        { name: "qty" },
        { name: "unit_price" },
      ],
    },
  },
  {
    id: "tbl-channels",
    type: "table",
    position: { x: 0, y: 0 },
    data: {
      title: "Channels",
      fields: [
        { name: "channel_id", keyType: "PK" },
        { name: "name" },
        { name: "type" },
        { name: "cost_per_lead" },
      ],
    },
  },
];


// ─── Ontologies (8) ──────────────────────────────────────────────────────────
const ontologies: KgFlowNode[] = [
  {
    id: "ont-batch",
    type: "ontology",
    position: { x: 0, y: 0 },
    data: { title: "Batch" },
  },
  {
    id: "ont-customer",
    type: "ontology",
    position: { x: 0, y: 0 },
    data: { title: "Customer" },
  },
  {
    id: "ont-region",
    type: "ontology",
    position: { x: 0, y: 0 },
    data: { title: "Region" },
  },
  {
    id: "ont-product",
    type: "ontology",
    position: { x: 0, y: 0 },
    data: { title: "Product" },
  },
  {
    id: "ont-order",
    type: "ontology",
    position: { x: 0, y: 0 },
    data: { title: "Order" },
  },
  {
    id: "ont-campaign",
    type: "ontology",
    position: { x: 0, y: 0 },
    data: { title: "Campaign" },
  },
  {
    id: "ont-employee",
    type: "ontology",
    position: { x: 0, y: 0 },
    data: { title: "Employee" },
  },
  {
    id: "ont-channel",
    type: "ontology",
    position: { x: 0, y: 0 },
    data: { title: "Channel" },
  },
];

// ─── Data models (1) ─────────────────────────────────────────────────────────
const dataModels: KgFlowNode[] = [
  {
    id: "dm-order",
    type: "dataModel",
    position: { x: 0, y: 0 },
    data: { title: "Order" },
  },
];

// ─── Metrics (20) ────────────────────────────────────────────────────────────
const metrics: KgFlowNode[] = [
  {
    id: "m-gm",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Gross margin" },
  },
  {
    id: "m-aov",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Avg order value" },
  },
  {
    id: "m-oc",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Order count" },
  },
  {
    id: "m-conversion",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Conversion rate" },
  },
  {
    id: "m-cac",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "CAC" },
  },
  {
    id: "m-ltv",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Customer LTV" },
  },
  {
    id: "m-churn",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Churn rate" },
  },
  {
    id: "m-mrr",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "MRR" },
  },
  {
    id: "m-nps",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "NPS" },
  },
  {
    id: "m-rev-growth",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Revenue growth" },
  },
  {
    id: "m-inv-turn",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Inventory turnover" },
  },
  {
    id: "m-fulfillment",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Fulfillment rate" },
  },
  {
    id: "m-cust-acq",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Customer acquisition" },
  },
  {
    id: "m-return-rate",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Return rate" },
  },
  {
    id: "m-profit-margin",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Profit margin" },
  },
  {
    id: "m-sales-velocity",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Sales velocity" },
  },
  {
    id: "m-emp-productivity",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Employee productivity" },
  },
  {
    id: "m-campaign-roi",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Campaign ROI" },
  },
  {
    id: "m-support-tickets",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Support tickets" },
  },
  {
    id: "m-time-to-resolve",
    type: "metric",
    position: { x: 0, y: 0 },
    data: { title: "Time to resolve" },
  },
];

// ─── Reports (12) ────────────────────────────────────────────────────────────
const reports: KgFlowNode[] = [
  {
    id: "rpt-gross",
    type: "report",
    position: { x: 0, y: 0 },
    data: {
      title: "Gross sales KPI",
      biName: "MagicBI",
      biLogoUrl: "/logos/magicbi.svg",
      reportSourceType: "magicbi",
    },
  },
  {
    id: "rpt-returns",
    type: "report",
    position: { x: 0, y: 0 },
    data: {
      title: "Returns policy v3.2",
      biName: "Confluence",
      biLogoUrl: "/logos/confluence.svg",
      reportSourceType: "retrieved",
    },
  },
  {
    id: "rpt-trend",
    type: "report",
    position: { x: 0, y: 0 },
    data: {
      title: "Sales trend",
      biName: "Tableau",
      biLogoUrl: "/logos/tableau.svg",
      reportSourceType: "datasource",
    },
  },
  {
    id: "rpt-q3-orders",
    type: "report",
    position: { x: 0, y: 0 },
    data: {
      title: "Q3 orders",
      biName: "MagicBI",
      biLogoUrl: "/logos/magicbi.svg",
      reportSourceType: "magicbi",
    },
  },
  {
    id: "rpt-cust-acq",
    type: "report",
    position: { x: 0, y: 0 },
    data: {
      title: "Customer acquisition",
      biName: "MagicBI",
      biLogoUrl: "/logos/magicbi.svg",
      reportSourceType: "magicbi",
    },
  },
  {
    id: "rpt-inventory",
    type: "report",
    position: { x: 0, y: 0 },
    data: {
      title: "Inventory status",
      biName: "Tableau",
      biLogoUrl: "/logos/tableau.svg",
      reportSourceType: "datasource",
    },
  },
  {
    id: "rpt-employees",
    type: "report",
    position: { x: 0, y: 0 },
    data: {
      title: "Employee performance",
      biName: "MagicBI",
      biLogoUrl: "/logos/magicbi.svg",
      reportSourceType: "magicbi",
    },
  },
  {
    id: "rpt-campaigns",
    type: "report",
    position: { x: 0, y: 0 },
    data: {
      title: "Campaign performance",
      biName: "MagicBI",
      biLogoUrl: "/logos/magicbi.svg",
      reportSourceType: "magicbi",
    },
  },
  {
    id: "rpt-churn",
    type: "report",
    position: { x: 0, y: 0 },
    data: {
      title: "Churn analysis",
      biName: "Tableau",
      biLogoUrl: "/logos/tableau.svg",
      reportSourceType: "datasource",
    },
  },
  {
    id: "rpt-finance",
    type: "report",
    position: { x: 0, y: 0 },
    data: {
      title: "Finance summary",
      biName: "MagicBI",
      biLogoUrl: "/logos/magicbi.svg",
      reportSourceType: "magicbi",
    },
  },
  {
    id: "rpt-marketing",
    type: "report",
    position: { x: 0, y: 0 },
    data: {
      title: "Marketing dashboard",
      biName: "Tableau",
      biLogoUrl: "/logos/tableau.svg",
      reportSourceType: "datasource",
    },
  },
  {
    id: "rpt-ops",
    type: "report",
    position: { x: 0, y: 0 },
    data: {
      title: "Operations overview",
      biName: "MagicBI",
      biLogoUrl: "/logos/magicbi.svg",
      reportSourceType: "magicbi",
    },
  },
];

// ─── Data Stories (6) ────────────────────────────────────────────────────────
const dataStories: KgFlowNode[] = [
  {
    id: "dstory-q3",
    type: "dataStory",
    position: { x: 0, y: 0 },
    data: { title: "Q3 revenue review", status: "Published" },
  },
  {
    id: "dstory-annual",
    type: "dataStory",
    position: { x: 0, y: 0 },
    data: { title: "Annual review", status: "Published" },
  },
  {
    id: "dstory-customer",
    type: "dataStory",
    position: { x: 0, y: 0 },
    data: { title: "Customer insights", status: "Published" },
  },
  {
    id: "dstory-mktg",
    type: "dataStory",
    position: { x: 0, y: 0 },
    data: { title: "Marketing performance", status: "Draft" },
  },
  {
    id: "dstory-supply",
    type: "dataStory",
    position: { x: 0, y: 0 },
    data: { title: "Supply chain report", status: "Draft" },
  },
  {
    id: "dstory-exec",
    type: "dataStory",
    position: { x: 0, y: 0 },
    data: { title: "Executive brief", status: "Published" },
  },
];

// ─── Artifacts (18) ──────────────────────────────────────────────────────────
const artifacts: KgFlowNode[] = [
  {
    id: "art-q3-ppt",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Q3 revenue deck", kind: "PPT" },
  },
  {
    id: "art-q3-video",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Q3 revenue report", kind: "Video" },
  },
  {
    id: "art-q3-memo",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Q3 exec memo", kind: "Memo" },
  },
  {
    id: "art-q3-slack",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Q3 highlights slack", kind: "Slack" },
  },
  {
    id: "art-annual-ppt",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Annual review", kind: "PPT" },
  },
  {
    id: "art-annual-pdf",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Annual review", kind: "PDF" },
  },
  {
    id: "art-cust-ppt",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Customer insights", kind: "PPT" },
  },
  {
    id: "art-cust-video",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Customer insights", kind: "Video" },
  },
  {
    id: "art-mktg-ppt",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Marketing performance", kind: "PPT" },
  },
  {
    id: "art-mktg-pdf",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Marketing performance", kind: "PDF" },
  },
  {
    id: "art-supply-memo",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Supply chain report", kind: "Memo" },
  },
  {
    id: "art-supply-ppt",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Supply chain report", kind: "PPT" },
  },
  {
    id: "art-exec-ppt",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Executive brief", kind: "PPT" },
  },
  {
    id: "art-exec-doc",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Executive brief", kind: "Doc" },
  },
  {
    id: "art-churn-video",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Churn analysis", kind: "Video" },
  },
  {
    id: "art-fin-pdf",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Finance summary", kind: "PDF" },
  },
  {
    id: "art-ops-memo",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Operations overview", kind: "Memo" },
  },
  {
    id: "art-inv-doc",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Inventory status", kind: "Doc" },
  },
  {
    id: "art-emp-ppt",
    type: "artifact",
    position: { x: 0, y: 0 },
    data: { title: "Employee performance", kind: "PPT" },
  },
];

// ─── All Nodes — base 96 + stress 218 = 314 total ──────────────────────────
export const sampleNodes: KgFlowNode[] = [
  ...dataSources,      //   6
  ...schemas,          //   3
  ...stressSchemas,    //   1  (BigQuery schema)
  ...tables,           //  22
  ...stressTables,     //  37  (grid overflow per schema)
  ...dataModels,       //   1
  ...ontologies,       //   8
  ...stressOntologies, //  40  (column wrap past wrapBottomY)
  ...metrics,          //  20
  ...stressMetrics,    //  45
  ...reports,          //  12
  ...stressReports,    //  35
  ...dataStories,      //   6
  ...stressDataStories,//  25
  ...artifacts,        //  18
  ...stressArtifacts,  //  35
]; // = 314

// ─── Edge definitions ────────────────────────────────────────────────────────
//
// Shape matches what the backend graph API returns.
// Each object has { source, target, label } — identical to the API response.
// When you wire up the real API, replace sampleEdges with:
//
//   const { nodes, edges } = await fetchKgGraph(workspaceId);
//   // edges already carry `label` from the server — pass them straight to <KgCanvas>
//
// The `tier()` helper assigns one relationship label to an entire group of
// source→target pairs, mirroring how the backend groups edges by relationship type.

interface ApiEdgeDef {
  source: string;
  target: string;
  /** Relationship type — comes from the backend graph API in production. */
  label: string;
}

const tier = (label: string, pairs: [string, string][]): ApiEdgeDef[] =>
  pairs.map(([source, target]) => ({ source, target, label }));

// Mock API response — each tier group has its own relationship label
const allEdgeDefs: ApiEdgeDef[] = [
  // Schema-bearing data sources connect DS -> schema first…
  ...tier("contains", [
    ["ds-snowflake", "sch-sf-analytics"],
    ["ds-snowflake", "sch-sf-staging"],
    ["ds-postgres",  "sch-pg-public"],
  ]),
  // …then schema -> table for those, while schema-less sources keep DS -> table.
  ...tier("ingests", [
    // Snowflake → Analytics schema
    ["sch-sf-analytics", "tbl-orders"],
    ["sch-sf-analytics", "tbl-products"],
    ["sch-sf-analytics", "tbl-customers"],
    // Snowflake → Staging schema
    ["sch-sf-staging",   "tbl-inventory"],
    ["sch-sf-staging",   "tbl-suppliers"],
    ["sch-sf-staging",   "tbl-transactions"],
    // Postgres → Public schema
    ["sch-pg-public",    "tbl-employees"],
    ["sch-pg-public",    "tbl-departments"],
    ["sch-pg-public",    "tbl-invoices"],
    ["sch-pg-public",    "tbl-payments"],
    ["sch-pg-public",    "tbl-subscriptions"],
    ["sch-pg-public",    "tbl-returns"],
    ["sch-pg-public",    "tbl-channels"],
    // Schema-less data sources connect directly to tables
    ["ds-tableau",   "tbl-sales-report"],
    ["ds-tableau",   "tbl-campaigns"],
    ["ds-bigquery",  "tbl-sessions"],
    ["ds-bigquery",  "tbl-events"],
    ["ds-bigquery",  "tbl-page-views"],
    ["ds-dbt",       "tbl-order-items"],
    ["ds-dbt",       "tbl-product-catalog"],
    ["ds-s3",        "tbl-shipments"],
    ["ds-s3",        "tbl-warehouses"],
  ]),
  // One demo data model sits between its source tables and the ontology it
  // feeds (used to exercise the data model inspector). Existing table→ontology
  // lineage below is untouched.
  ...tier("models", [
    ["tbl-orders",      "dm-order"],
    ["tbl-order-items", "dm-order"],
  ]),
  // Tables connect directly to ontologies (dataModel tier removed).
  // Preserves the same semantic lineage — each table feeds the ontologies
  // it would have reached through its former data model.
  ...tier("classifies", [
    ["dm-order",            "ont-order"],
    ["tbl-orders",          "ont-order"],
    ["tbl-orders",          "ont-batch"],
    ["tbl-order-items",     "ont-order"],
    ["tbl-order-items",     "ont-batch"],
    ["tbl-sales-report",    "ont-order"],
    ["tbl-transactions",    "ont-order"],
    ["tbl-invoices",        "ont-order"],
    ["tbl-payments",        "ont-order"],
    ["tbl-customers",       "ont-customer"],
    ["tbl-customers",       "ont-region"],
    ["tbl-subscriptions",   "ont-customer"],
    ["tbl-sessions",        "ont-customer"],
    ["tbl-products",        "ont-product"],
    ["tbl-product-catalog", "ont-product"],
    ["tbl-inventory",       "ont-product"],
    ["tbl-inventory",       "ont-batch"],
    ["tbl-warehouses",      "ont-batch"],
    ["tbl-shipments",       "ont-batch"],
    ["tbl-shipments",       "ont-region"],
    ["tbl-returns",         "ont-batch"],
    ["tbl-campaigns",       "ont-campaign"],
    ["tbl-channels",        "ont-channel"],
    ["tbl-events",          "ont-campaign"],
    ["tbl-page-views",      "ont-campaign"],
    ["tbl-page-views",      "ont-channel"],
    ["tbl-employees",       "ont-employee"],
    ["tbl-departments",     "ont-employee"],
    ["tbl-suppliers",       "ont-region"],
  ]),
  ...tier("measures", [
    ["ont-order",    "m-gm"],
    ["ont-order",    "m-aov"],
    ["ont-order",    "m-oc"],
    ["ont-order",    "m-return-rate"],
    ["ont-batch",    "m-sales-velocity"],
    ["ont-batch",    "m-fulfillment"],
    ["ont-batch",    "m-inv-turn"],
    ["ont-customer", "m-ltv"],
    ["ont-customer", "m-churn"],
    ["ont-customer", "m-nps"],
    ["ont-customer", "m-cust-acq"],
    ["ont-customer", "m-mrr"],
    ["ont-region",   "m-rev-growth"],
    ["ont-region",   "m-profit-margin"],
    ["ont-product",  "m-inv-turn"],
    ["ont-product",  "m-return-rate"],
    ["ont-campaign", "m-cac"],
    ["ont-campaign", "m-conversion"],
    ["ont-campaign", "m-campaign-roi"],
    ["ont-channel",  "m-campaign-roi"],
    ["ont-channel",  "m-cac"],
    ["ont-employee", "m-emp-productivity"],
    ["ont-employee", "m-support-tickets"],
    ["ont-employee", "m-time-to-resolve"],
  ]),
  ...tier("powers", [
    ["m-gm",               "rpt-gross"],
    ["m-aov",              "rpt-gross"],
    ["m-oc",               "rpt-q3-orders"],
    ["m-return-rate",      "rpt-returns"],
    ["m-sales-velocity",   "rpt-trend"],
    ["m-rev-growth",       "rpt-trend"],
    ["m-fulfillment",      "rpt-inventory"],
    ["m-inv-turn",         "rpt-inventory"],
    ["m-ltv",              "rpt-cust-acq"],
    ["m-churn",            "rpt-churn"],
    ["m-nps",              "rpt-cust-acq"],
    ["m-cust-acq",         "rpt-cust-acq"],
    ["m-mrr",              "rpt-finance"],
    ["m-profit-margin",    "rpt-finance"],
    ["m-cac",              "rpt-campaigns"],
    ["m-conversion",       "rpt-campaigns"],
    ["m-campaign-roi",     "rpt-marketing"],
    ["m-emp-productivity", "rpt-employees"],
    ["m-support-tickets",  "rpt-ops"],
    ["m-time-to-resolve",  "rpt-ops"],
  ]),
  ...tier("published in", [
    ["rpt-gross",     "dstory-q3"],
    ["rpt-trend",     "dstory-q3"],
    ["rpt-q3-orders", "dstory-q3"],
    ["rpt-returns",   "dstory-q3"],
    ["rpt-gross",     "dstory-annual"],
    ["rpt-finance",   "dstory-annual"],
    ["rpt-employees", "dstory-annual"],
    ["rpt-cust-acq",  "dstory-customer"],
    ["rpt-churn",     "dstory-customer"],
    ["rpt-campaigns", "dstory-mktg"],
    ["rpt-marketing", "dstory-mktg"],
    ["rpt-inventory", "dstory-supply"],
    ["rpt-ops",       "dstory-supply"],
    ["rpt-gross",     "dstory-exec"],
    ["rpt-finance",   "dstory-exec"],
    ["rpt-ops",       "dstory-exec"],
    ["rpt-employees", "dstory-exec"],
  ]),
  ...tier("exports to", [
    ["dstory-q3",       "art-q3-ppt"],
    ["dstory-q3",       "art-q3-video"],
    ["dstory-q3",       "art-q3-memo"],
    ["dstory-q3",       "art-q3-slack"],
    ["dstory-annual",   "art-annual-ppt"],
    ["dstory-annual",   "art-annual-pdf"],
    ["dstory-customer", "art-cust-ppt"],
    ["dstory-customer", "art-cust-video"],
    ["dstory-mktg",     "art-mktg-ppt"],
    ["dstory-mktg",     "art-mktg-pdf"],
    ["dstory-supply",   "art-supply-memo"],
    ["dstory-supply",   "art-supply-ppt"],
    ["dstory-exec",     "art-exec-ppt"],
    ["dstory-exec",     "art-exec-doc"],
    ["dstory-customer", "art-churn-video"],
    ["dstory-annual",   "art-fin-pdf"],
    ["dstory-exec",     "art-ops-memo"],
    ["dstory-supply",   "art-inv-doc"],
    ["dstory-annual",   "art-emp-ppt"],
  ]),
  // Layout stress-test extras (grid overflow + viewport column wrap)
  ...stressEdgeDefs,
];

const nodeTypeById = new Map(sampleNodes.map((n) => [n.id, n.type]));

export const sampleEdges: KgFlowEdge[] = allEdgeDefs.map((def, i) => {
  const sourceType = nodeTypeById.get(def.source) as KgFlowNodeType | undefined;
  const targetType = nodeTypeById.get(def.target) as KgFlowNodeType | undefined;
  const {
    source,
    target,
    sourceHandle,
    targetHandle,
    fromType,
    toType,
  } = resolveEdgeHandlesForEndpoints(
    def.source,
    def.target,
    sourceType,
    targetType,
  );
  const color = getEdgeColor(fromType, toType);
  return {
    id: `e-${i}`,
    source,
    target,
    sourceHandle,
    targetHandle,
    type: "default",
    // Label comes straight from the API edge definition — no client-side computation.
    // In production, replace this entire file with an API call that returns the same shape.
    label: def.label,
    labelStyle: {
      fontSize: 9,
      fill: color,
      fontWeight: 500,
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    },
    labelBgStyle: { fill: "#ffffff", fillOpacity: 0.85 },
    labelBgPadding: [3, 4] as [number, number],
    labelBgBorderRadius: 3,
    style: { stroke: color, strokeWidth: 1.5 },
  };
});

// ─── Dev console: final rendered edge attributes ──────────────────────────────
if (process.env.NODE_ENV === "development") {
  const byLabel = sampleEdges.reduce<Record<string, number>>((acc, e) => {
    const lbl = String(e.label ?? "—");
    acc[lbl] = (acc[lbl] ?? 0) + 1;
    return acc;
  }, {});

  console.groupCollapsed(
    `[KG Canvas] ${sampleNodes.length} nodes · ${sampleEdges.length} edges loaded`,
  );
  console.log("Relationship breakdown:");
  console.table(byLabel);
  console.log("Sample rendered edge attributes (first edge):", {
    id:            sampleEdges[0].id,
    source:        sampleEdges[0].source,
    target:        sampleEdges[0].target,
    label:         sampleEdges[0].label,
    strokeColor:   (sampleEdges[0].style as { stroke?: string } | undefined)?.stroke,
    strokeWidth:   (sampleEdges[0].style as { strokeWidth?: number } | undefined)?.strokeWidth,
    labelFontSize: (sampleEdges[0].labelStyle as { fontSize?: number } | undefined)?.fontSize,
    labelBgOpacity:(sampleEdges[0].labelBgStyle as { fillOpacity?: number } | undefined)?.fillOpacity,
    labelBgRadius: sampleEdges[0].labelBgBorderRadius,
  });
  console.groupEnd();
}
